import React, { useState } from 'react';
import { Sidebar } from './Dashboard';

export default function Help() {
  return (
    <div className="page">
      <Sidebar />
      <div className="main">
        <div className="page-title">Help — how Creator Protect works</div>
        <p style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 760, marginBottom: 30, lineHeight: 1.6 }}>
          Every feature below is running in the background on your account. This page
          is a plain-language guide to what each one does, what it protects you from,
          and where to find its controls in the dashboard.
        </p>

        {FEATURES.map(f => <FeatureSection key={f.id} feature={f} />)}

        <div style={{ marginTop: 40, padding: 20, background: '#111', border: '1px solid #222', borderRadius: 12, color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
          Still stuck? Email <a href="mailto:support@creatorprotect.io" style={{ color: '#a78bfa' }}>support@creatorprotect.io</a> with
          your account email and a short description — we read every message.
        </div>
      </div>
    </div>
  );
}

function FeatureSection({ feature }) {
  const [open, setOpen] = useState(feature.defaultOpen !== false);
  return (
    <div style={{ background: '#111', border: '1px solid #222', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', padding: '18px 22px', textAlign: 'left',
          background: 'transparent', border: 'none', color: '#fff',
          display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 20 }}>{feature.icon}</span>
        <span style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{feature.title}</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{feature.tagline}</div>
        </span>
        <span style={{ fontSize: 14, color: '#94a3b8' }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 22px 22px 56px', color: '#cbd5e1', fontSize: 14, lineHeight: 1.7 }}>
          <p style={{ marginBottom: 12 }}>{feature.what}</p>
          {feature.howto && (
            <>
              <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, marginTop: 14 }}>
                Where to find it
              </div>
              <p style={{ marginBottom: 12 }}>{feature.howto}</p>
            </>
          )}
          {feature.bullets && (
            <>
              <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, marginTop: 14 }}>
                What it catches
              </div>
              <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
                {feature.bullets.map((b, i) => <li key={i} style={{ marginBottom: 4 }}>{b}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const FEATURES = [
  {
    id: 'watermarking',
    icon: '🎞',
    title: 'Forensic watermarking (coming soon)',
    tagline: "When this ships, every video delivered will be traceable to the buyer whose copy leaked.",
    what: `When watermarking goes live, every video you upload will carry an audio watermark (WavMark) embedded at upload time — the creator-level mark that always travels with the file. On buyer purchase, a second layer will encode the purchase ID before delivery. If the video ever surfaces on a leak site, we'll decode the watermark from the leaked audio and trace it back to the exact purchase + buyer.`,
    howto: `Will turn on automatically for every PPV upload once released. Decoded buyer attributions will appear under My Vault → Known leakers.`,
    bullets: [
      'Base layer — will identify the creator even on pre-purchase previews',
      'Buyer layer — will identify the specific purchase on leaks',
      'Will survive recompression, platform re-encodes, and partial clips',
    ],
  },
  {
    id: 'dmca',
    icon: '📜',
    title: 'DMCA engine',
    tagline: 'Takedowns fire automatically the moment a leak is found.',
    what: `Every leak detection runs through a decision engine that figures out the right DMCA recipient for the host — specific agents for Bunkr, Coomer, Reddit, Pornhub, tube sites, pastebins, etc., with a fallback email discovered via WHOIS. Notices go out the same day and the takedown tracker logs every response and removal.`,
    howto: `Leaks tab shows every detection and its DMCA status. Takedowns tab shows the timeline of filed notices and removals.`,
    bullets: [
      'Platform-specific DMCA forms for sites that reject email (Pornhub, etc.)',
      'Automatic re-filing when the host fails to act',
      'Weekly summary email with what got removed',
    ],
  },
  {
    id: 'crawler',
    icon: '🕸',
    title: '30+ piracy-platform leak crawler',
    tagline: 'Scans the usual leak hangouts every night looking for your content.',
    what: `A scheduled crawler runs across 30+ piracy platforms — tube sites, image boards, paste sites, dedicated leak hosts — looking for your username and content fingerprints. Google CSE handles the long tail. New detections surface in the Leaks tab and auto-queue for DMCA.`,
    howto: `Settings → Usernames to add any handles you want scanned. Google Search Console deindex runs alongside — every confirmed leak URL gets submitted for Google removal too.`,
    bullets: [
      'Username-based search across 30+ piracy platforms nightly',
      'Perceptual-hash match against your uploaded fingerprint catalog',
      'Subscriber-scanning via Dropbox for leak-suspect comparisons',
    ],
  },
  {
    id: 'face-protect',
    icon: '🧑',
    title: 'Face Protect (coming soon)',
    tagline: 'Will detect fake accounts impersonating you across social.',
    what: `When Face Protect ships, you'll enroll your face once (a few reference photos). A daily job will scan social platforms — Instagram, X, TikTok, Telegram — for accounts using your face to scam fans. Matches will surface on the dashboard with a confidence score and direct links to the fake profile for takedown.`,
    howto: `Will live under the Face Protect tab in the sidebar once released. First use will prompt you to enroll; after that it will be fully hands-off.`,
    bullets: [
      'Will catch impersonation accounts on Instagram, X, TikTok, Telegram',
      'Will catch AI-generated deepfakes trained on your face',
      'Will catch catfish profiles scamming your audience',
    ],
  },
  {
    id: 'vaultdirect',
    icon: '💎',
    title: 'VaultDirect — PPV + subscriptions',
    tagline: 'Sell your own content at your own prices, paid direct.',
    what: `VaultDirect is your direct-to-fan marketplace at vaultdirect.io/<your-username>. Upload once, set a price, Stripe handles checkout, and (once per-buyer watermarking ships) delivered copies will carry a watermark tied to the buyer. You can also enable a monthly subscription tier so fans unlock everything for a recurring fee. Payouts go straight to your bank via Stripe Connect — platform takes 10%.`,
    howto: `Dashboard → My Vault tab. Upload new, set prices, enable subscriptions, track sales and subscribers. Share links from My Vault post-launch.`,
    bullets: [
      'One-time PPV purchases (buyer watermarking coming soon)',
      'Monthly subscriptions with full-storefront unlock',
      'Affiliate program — share a link, earn on referrals',
      'Stripe Connect direct deposit to your bank',
    ],
  },
  {
    id: 'threat-monitoring',
    icon: '⚠',
    title: 'Threat monitoring — dox / paste / dark web (coming soon)',
    tagline: 'Will alert you the moment your info appears somewhere dangerous.',
    what: `When this ships, three parallel scanners will run every few minutes: dox detection (your legal name, address, phone surfacing on forums), paste-leak monitoring (Pastebin, Ghostbin, etc.), and dark-web crawls. Any hit will land on the dashboard's threat panel with severity, context snippet, and a one-click DMCA starter.`,
    howto: `The threat panel will live in the dashboard sidebar once released — a red dot when you have unacknowledged high-severity hits. Acknowledge to park, resolve once handled.`,
    bullets: [
      'Will catch dox posts with your real name, address, or phone',
      'Paste-bin leaks of subscriber emails or content',
      'Will catch dark-web marketplace listings of scraped creator data',
    ],
  },
  {
    id: 'sms-alerts',
    icon: '📱',
    title: 'SMS alerts',
    tagline: 'Critical events buzz your phone, not just your inbox.',
    what: `High-severity detections (new leaks on high-traffic sites, and — once threat monitoring ships — dox hits and dark-web posts) trigger an SMS within a minute of detection. Routine stuff (new DMCA filed, nightly summary) stays in email so you're not drowning in texts.`,
    howto: `Settings → Phone number to opt in. You can turn it off any time; Twilio charges the platform, not you.`,
  },
];
